import { HolderOutlined } from '@ant-design/icons';
import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, List, Switch, Tooltip } from 'antd';
import React, { createContext, useContext, useMemo } from 'react';

import { RedirectIcon } from 'app/components/pages/Hotels/RedirectIcon';

import './SListItemCard.scss';

type SortableListItemContextProps = {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
  attributes?: DraggableAttributes;
};

const SortableListItemContext = createContext<SortableListItemContextProps>({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners, attributes } = useContext(
    SortableListItemContext
  );

  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
    />
  );
};

const SortableListItem: React.FC<
  React.ComponentProps<typeof List.Item> & { itemKey: number }
> = (props) => {
  const { itemKey, style, ...rest } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemKey });

  const listStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const memoizedValue = useMemo<SortableListItemContextProps>(
    () => ({ setActivatorNodeRef, listeners, attributes }),
    [setActivatorNodeRef, listeners, attributes]
  );

  return (
    <SortableListItemContext.Provider value={memoizedValue}>
      <List.Item {...rest} ref={setNodeRef} style={listStyle} />
    </SortableListItemContext.Provider>
  );
};

type Props = {
  itemId: number;
  title: JSX.Element | string;
  children: JSX.Element;
  checked: boolean;
  onToggle: (value: boolean) => void;
  disableToggle: boolean;
  displayDragHandle?: boolean;
  url?: string;
};

export const SListItemCard = ({
  itemId,
  title,
  children,
  checked,
  onToggle,
  disableToggle,
  displayDragHandle,
  url,
}: Props) => {
  return (
    <SortableListItem key={itemId} itemKey={itemId}>
      <div className="slist-item-card">
        <div className="slist-item-card__header">
          <div className="slist-item-card__header-left">
            <Switch
              disabled={disableToggle}
              checked={checked}
              onChange={onToggle}
              size="small"
            />
            {title}
          </div>
          {(url || displayDragHandle) && (
            <div className="slist-item-card__header-right">
              {url && (
                <Tooltip placement="top" title="Open experience page">
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <RedirectIcon />
                  </a>
                </Tooltip>
              )}
              {displayDragHandle && <DragHandle />}
            </div>
          )}
        </div>

        {checked && children}
      </div>
    </SortableListItem>
  );
};
